import { memo } from 'react';
import { FileText, Pencil } from 'lucide-react';
import { Button } from '../ui';
import { HtmlEditor } from './HtmlEditor';

interface TranslatedContentViewerProps {
  isCaseTranslating: boolean;
  isGeneratingPdf: boolean;
  hasTranslation: boolean;
  hasPdf: boolean;
  viewMode: 'pdf' | 'html';
  htmlLoading: boolean;
  htmlError: string | null;
  htmlContent: string | null;
  translatedPdfLoading: boolean;
  translatedPdfUrl: string | null;
  isApproved: boolean;
  onSaveHtml: (html: string) => Promise<void>;
  onSetViewMode: (mode: 'pdf' | 'html') => void;
}

// Memoized Translated Content Viewer
export const TranslatedContentViewer = memo(function TranslatedContentViewer({
  isCaseTranslating,
  isGeneratingPdf,
  hasTranslation,
  hasPdf,
  viewMode,
  htmlLoading,
  htmlError,
  htmlContent,
  translatedPdfLoading,
  translatedPdfUrl,
  isApproved,
  onSaveHtml,
  onSetViewMode,
}: TranslatedContentViewerProps) {
  // Translating state
  if (isCaseTranslating) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-dark-recessed">
        <div className="text-center">
          <span className="inline-block w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-lg font-medium text-gray-600 dark:text-gray-300">Translating...</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">This may take a few minutes</p>
        </div>
      </div>
    );
  }

  // Generating PDF state
  if (isGeneratingPdf) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-dark-recessed">
        <div className="text-center">
          <span className="inline-block w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-lg font-medium text-gray-600 dark:text-gray-300">Generating PDF...</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Please wait</p>
        </div>
      </div>
    );
  }

  // No translation yet
  if (!hasTranslation) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-dark-recessed">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-lg font-medium">Translation not yet available</p>
          <p className="text-sm">Click "Translate" to begin</p>
        </div>
      </div>
    );
  }

  // HTML Editor view
  if (viewMode === 'html') {
    if (htmlLoading) {
      return (
        <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-dark-recessed">
          <div className="text-center">
            <span className="inline-block w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mb-2" />
            <p className="text-gray-500 dark:text-gray-400">Loading HTML...</p>
          </div>
        </div>
      );
    }

    if (htmlError) {
      return (
        <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-dark-recessed">
          <p className="text-red-500 dark:text-red-400">{htmlError}</p>
        </div>
      );
    }

    if (htmlContent) {
      return (
        <HtmlEditor
          html={htmlContent}
          onSave={onSaveHtml}
          readOnly={isApproved}
        />
      );
    }
  }

  // PDF view (default)
  if (translatedPdfLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-dark-recessed">
        <div className="text-center">
          <span className="inline-block w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mb-2" />
          <p className="text-gray-500 dark:text-gray-400">Loading PDF...</p>
        </div>
      </div>
    );
  }

  if (translatedPdfUrl && hasPdf) {
    // Add zoom parameter to default to 80% zoom
    const pdfUrl = translatedPdfUrl.includes('#') ? translatedPdfUrl : `${translatedPdfUrl}#zoom=80`;
    return (
      <iframe
        src={pdfUrl}
        className="w-full h-full border-0"
        title="Translated Document"
      />
    );
  }

  // Translated but PDF not ready yet
  if (hasTranslation && !hasPdf) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-dark-recessed">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
          <p className="text-lg font-medium">PDF being generated...</p>
          <p className="text-sm">Switch to HTML view to see the translation</p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-4"
            onClick={() => onSetViewMode('html')}
          >
            <Pencil className="w-4 h-4 mr-1" />
            View HTML
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-dark-recessed">
      <p className="text-gray-500 dark:text-gray-400">No translated PDF available</p>
    </div>
  );
});
